import React, { useMemo, useState } from 'react';
import { omit } from 'lodash';
import { styled } from '@mui/material';

import { useUser2 } from 'contexts/user2';
import { Button, Checkbox, Dropdown, IconButton, SplitButton, Stack } from 'shell/components';

const DrawerActions = ({
    milestone,
    milestoneState,
    changes,
    setFiles,
    setSharedUrls,
    setChanges,
    setView,
    setActiveTemplateId,
    setScheduleModal,
    save,
    publish,
    unpublish,
    unschedule,
    deleteMilestone,
    setMilestoneDrawer,
    setConfirmationModal,
    closeConfirmationModal,
}) => {
    const { formatives } = useUser2();

    const [shouldNotify, setShouldNotify] = useState(false);

    const formative = useMemo(() => {
        return formatives?.find(({ milestone_id }) => milestone_id === milestone.id);
    }, [formatives, milestone.id]);

    const publishMilestone = () => {
        setConfirmationModal({
            visible: true,
            destructive: false,
            title: 'Publish Milestone Now?',
            content: 'The linked task templates will also be published.',
            confirmText: 'Publish',
            onConfirm: () => {
                closeConfirmationModal();
                publish(milestone.id, changes);
            },
            onCancel: closeConfirmationModal,
        });
    };

    const unpublishMilestone = () => {
        setConfirmationModal({
            visible: true,
            destructive: true,
            title: 'Unpublish Milestone?',
            content: `Tasks associated will also be unpublished.`,
            confirmText: 'Unpublish',
            onConfirm: () => {
                closeConfirmationModal();
                unpublish(milestone.id);
            },
            onCancel: closeConfirmationModal,
        });
    };

    const unscheduleMilestone = () => {
        setConfirmationModal({
            visible: true,
            destructive: false,
            title: 'Unschedule Milestone?',
            content: `The milestone will be unscheduled and reverted to a draft.`,
            confirmText: 'Unschedule',
            onConfirm: () => {
                closeConfirmationModal();
                unschedule(milestone.id);
            },
            onCancel: closeConfirmationModal,
        });
    };

    const _deleteMilestone = () => {
        setConfirmationModal({
            visible: true,
            destructive: true,
            title: 'Delete Milestone and Tasks Associated With This Milestone?',
            content:
                'This action cannot be undone. To delete the milestone only, unlink the tasks associated from the milestone.',
            confirmText: 'Delete',
            onConfirm: () => {
                closeConfirmationModal();
                setMilestoneDrawer({ isOpen: false, milestoneId: '' });
                deleteMilestone(milestone.id);
            },
            onCancel: closeConfirmationModal,
        });
    };

    const undoResourcesChanges = (changes) => {
        const { resources } = changes;
        if (!resources) return;
        resources.forEach((resource) => {
            switch (resource.type) {
                case 'file':
                    setFiles((current) => {
                        return {
                            ...current,
                            [resource.id]: { ...current[resource.id], active: resource.action === 'delete' },
                        };
                    });
                    break;
                case 'link':
                    setSharedUrls((current) => {
                        return {
                            ...current,
                            [resource.id]: { ...current[resource.id], active: resource.action === 'delete' },
                        };
                    });
                    break;
                default:
                    break;
            }
        });
    };

    const handleClickSplitButton = ({ key }) => {
        const performKeyAction = (key) => {
            switch (key) {
                case 'publish':
                    publishMilestone();
                    break;
                case 'save':
                    if (milestone.isLocal) {
                        save(milestone.id);
                    } else {
                        if (milestoneState === 'draft' || milestoneState === 'scheduled') {
                            save(milestone.id, changes);
                            setView('milestone');
                            setActiveTemplateId('');
                        }
                        if (milestoneState === 'published') {
                            save(milestone.id, changes, shouldNotify);
                            setView('milestone');
                            setActiveTemplateId('');
                        }
                    }
                    break;
                case 'schedule':
                    setScheduleModal({ visible: true });
                    break;
                case 'unschedule':
                    unscheduleMilestone();
                    break;
            }
        };

        if (
            ['publish', 'save', 'schedule'].includes(key) &&
            (changes.allow_ai_feedback ?? milestone.allow_ai_feedback) &&
            formative?.status !== 'complete'
        ) {
            setConfirmationModal({
                visible: true,
                destructive: true,
                title: 'Incomplete Setup: AI Formative Feedback',
                content:
                    'If you continue, students won’t be able to use this feature. Please note that you can return later to complete the setup.',
                confirmText: 'Continue',
                onConfirm: () => {
                    closeConfirmationModal();
                    performKeyAction(key);
                },
                onCancel: closeConfirmationModal,
            });
        } else {
            performKeyAction(key);
        }
    };

    const getSplitButtonOptions = () => {
        if (milestone.isLocal) {
            return [
                { key: 'publish', value: 'Publish' },
                { key: 'schedule', value: 'Schedule' },
                { key: 'save', value: 'Save as Draft' },
            ];
        }
        if (milestoneState === 'draft') {
            if (Object.keys(omit(changes, ['published_on'])).length) {
                return [
                    { key: 'save', value: 'Save Changes' },
                    { key: 'publish', value: 'Save & Publish' },
                    { key: 'schedule', value: 'Schedule' },
                ];
            }
            return [
                { key: 'publish', value: 'Publish' },
                { key: 'schedule', value: 'Schedule' },
            ];
        }
        if (milestoneState === 'scheduled') {
            if (Object.keys(omit(changes, ['published_on'])).length) {
                return [
                    { key: 'save', value: 'Save Changes' },
                    { key: 'unschedule', value: 'Unschedule' },
                    { key: 'schedule', value: 'Reschedule' },
                    { key: 'publish', value: 'Publish' },
                ];
            }
            return [
                { key: 'unschedule', value: 'Unschedule' },
                { key: 'schedule', value: 'Reschedule' },
                { key: 'publish', value: 'Publish' },
            ];
        }
    };

    const hasChanges = Boolean(Object.keys(omit(changes, ['published_on'])).length);

    let isActive = false;
    if (milestone.isLocal) {
        isActive = Boolean(milestone.title);
    } else {
        isActive = milestoneState === 'published' ? hasChanges : true;
        isActive = Object.keys(changes).includes('title') ? Boolean(changes.title) : isActive;
    }

    const isDisabled = !isActive;

    return (
        <Stack
            sx={{ width: '100%' }}
            alignItems={'center'}
            direction={'row-reverse'}
            justifyContent={'space-between'}
            spacing={4}
        >
            <Stack alignItems={'center'} direction={'row'} spacing={2}>
                {hasChanges && (
                    <Button
                        variant={'secondary'}
                        onClick={() => {
                            setView('milestone');
                            setActiveTemplateId('');
                            undoResourcesChanges(changes);
                            setChanges({});
                        }}
                    >
                        Cancel
                    </Button>
                )}
                {milestoneState === 'published' ? (
                    <Button disabled={isDisabled} onClick={() => handleClickSplitButton({ key: 'save' })}>
                        Save
                    </Button>
                ) : (
                    <SplitButton options={getSplitButtonOptions()} onClick={handleClickSplitButton} />
                )}
                <Dropdown
                    id={'milestone-actions-dropdown'}
                    options={
                        milestoneState === 'published'
                            ? [
                                  { key: 'unpublish', value: 'Unpublish' },
                                  { key: 'delete', value: 'Unpublish & Delete' },
                              ]
                            : [{ key: 'delete', value: 'Delete' }]
                    }
                    rootComponent={
                        <IconButton
                            aria-label={'more options'}
                            kind={'moreHorizontal'}
                            size={'medium'}
                            variant={'square'}
                        />
                    }
                    onSelect={({ key }) => {
                        switch (key) {
                            case 'unpublish':
                                return unpublishMilestone();
                            case 'delete':
                                return _deleteMilestone();
                        }
                    }}
                />
            </Stack>
            {milestoneState === 'published' && hasChanges ? (
                <CustomCheckbox
                    checked={shouldNotify}
                    label={'Notify users of changes'}
                    onChange={() => setShouldNotify((current) => !current)}
                />
            ) : null}
        </Stack>
    );
};

const CustomCheckbox = styled(Checkbox)(() => {
    return {
        '& > .MuiTypography-root': {
            whiteSpace: 'unset',
        },
    };
});

export default DrawerActions;
